
import CmSvgIcon from '@/components/cm-svg-icon/cm-svg-icon.vue'
import { defineComponent, reactive, ref, toRefs, h, PropType } from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import BaseTree from '../../_components/group-tree/base-tree.vue'
import RegularPhysicalExamination from './_components/regular-physical-examination/regular-physical-examination.vue'
import TimingAntivirus from './_components/timing-antivirus/timing-antivirus.vue'
import FlowControl from './_components/flow-control/flow-control.vue'
import OnlineBlackAndWhiteList from './_components/online-black-and-white-list/online-black-and-white-list.vue'
import EarlyWarningAndMonitoring from './_components/early-warning-and-monitoring/early-warning-and-monitoring.vue'
import { GroupPolicySettingsService, SETTING_PANE_NAMES } from './group-policy-settings.service'
import LeakFix from '@/views/dashboard/_components/group-policy-settings/_components/leak-fix/leak-fix.vue'
import UsbManage from '@/views/dashboard/_components/group-policy-settings/_components/usb-manage/usb-manage.vue'
import BaselineScan from '@/views/dashboard/_components/group-policy-settings/_components/baseline-scan/baseline-scan.vue'
import { ElMessageBox } from 'element-plus'
import { LinuxDriverSwitch } from '@/config/switch'
import { OperateType } from '@/views/dashboard/_components/group-policy-settings/group-policy-drawer.service'
import BasicSettings
  from '@/views/dashboard/_components/group-policy-settings/_components/basic-settings/basic-settings.vue'
import SecurityProtection
  from '@/views/dashboard/_components/group-policy-settings/_components/security-protection/security-protection.vue'
import RealTimeProtection
  from '@/views/dashboard/_components/group-policy-settings/_components/real-time-protection/real-time-protection.vue'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'
import { useI18n } from 'vue-i18n'

enum TerminalType {
  Windows = 1,
  Linux = 2,
  Windows_Server = 3,
  Mac = 4
}

const SystemTypes = {
  1: [TerminalType.Windows, TerminalType.Windows_Server],
  2: [TerminalType.Linux],
  4: [TerminalType.Mac]
}

export default defineComponent({
  props: {
    policyId: {
      type: String,
      default: undefined
    },
    operateType: {
      type: String as PropType<'edit'|'add'|'read'>,
      default: 'edit'
    },
    activeTab: {
      type: String,
      default: SETTING_PANE_NAMES.basicSettings
    }
  },

  components: {
    RealTimeProtection,
    BaselineScan,
    UsbManage,
    LeakFix,
    BaseTree,
    RegularPhysicalExamination,
    TimingAntivirus,
    FlowControl,
    OnlineBlackAndWhiteList,
    EarlyWarningAndMonitoring,
    BasicSettings,
    SecurityProtection
  },

  setup (props, context) {
    const { t } = useI18n()
    const injector = useReflectiveInjector([GroupPolicySettingsService])
    const service = injector.get(GroupPolicySettingsService)
    const servicePermissionService = injector.get(ServicePermissionService)
    const systemTab = ref('1')
    const activeName = ref(props.activeTab || SETTING_PANE_NAMES.basicSettings)
    const linuxActiveName = ref('timing-antivirus')
    const macActiveName = ref('timing-antivirus')

    const data = reactive({
      flowHasPermission: servicePermissionService.getHasPermission(PermissionFe.flow_monitor),
      networkHasPermission: servicePermissionService.getHasPermission(PermissionFe.internet_url_limit),
      usbManageHasPermission: servicePermissionService.getHasPermission(PermissionFe.usb_peripheral_control),
      hasEdit: false
    })
    service.hasEditTab.subscribe((hasEdit: boolean) => {
      data.hasEdit = hasEdit
    })

    function cancel () {
      context.emit('cancel')
    }

    function save () {
      if (service.consumeTabSwitchSaving()) {
        return
      }
      context.emit('save')
    }

    return {
      IS_JP_ONLY_BUILD,
      HIDE_FUNC,
      OperateType,
      LinuxDriverSwitch,
      SETTING_PANE_NAMES,
      TerminalType,
      systemTab,
      activeName,
      linuxActiveName,
      macActiveName,
      SystemTypes,
      ...toRefs(data),
      cancel,
      save,
      async switchTab (newTab:string, oldTab:string) {
        if (data.hasEdit) {
          await ElMessageBox({
            title: t('groupPolicySettings.unsavedSwitchTitle'),
            iconClass: null,
            showCancelButton: true,
            message: h('div', { style: 'display:flex;align-items:center;' }, [
              h(CmSvgIcon, { name: 'views/dashboard/_components/group-policy-settings/_images/client_modal_tip', size: 48,color:'var(--cm-color-primary)', monochrome: true }),
              h('div', { style: 'margin-left:10px' }, [
                h('p', null, t('groupPolicySettings.unsavedSwitchMessage'))
              ])
            ]),
            confirmButtonText: t('groupPolicySettings.applyChanges'),
            cancelButtonText: t('common.cancel'),
          }).then(() => {
            service.updateEditTab(false)
            service.updateSaveTab(oldTab)
            return true
          }).catch(() => {
            service.updateEditTab(false)
            return true
          })
        }
      }
    }
  }
})
